import React, { useState, useMemo, useEffect } from 'react';
import { MainLayout } from '@/components/layouts/MainLayout';
import { PeriodFilter, PeriodFilterType, getDateRangeFromPeriod } from '@/components/common/PeriodFilter';
import { useWithdrawals } from '@/hooks/useWithdrawals';
import { useSupabaseInventory } from '@/context/SupabaseInventoryContext';
import { useCompany } from '@/context/CompanyContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { BarChart3, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EditableGrid } from '@/components/dashboard/EditableGrid';
import { EditModeToggle } from '@/components/dashboard/EditModeToggle';
import { AddWidgetButton } from '@/components/dashboard/AddWidgetButton';
import { AnalyticsWidget } from '@/components/analytics/AnalyticsWidget';
import { useDashboardLayout } from '@/hooks/useDashboardLayout';
import { secureLogger } from '@/utils/secureLogger';

export const RetraitAnalytics: React.FC = () => {
  const [periodFilter, setPeriodFilter] = useState<PeriodFilterType>('week');
  const [statusFilter, setStatusFilter] = useState<'all' | 'nouveau' | 'best_seller' | 'en_promotion' | 'fin_de_serie' | 'archive'>('all');
  const [rowsPerPage, setRowsPerPage] = useState<number>(20);
  const [currentPage, setCurrentPage] = useState<number>(1);
  
  const {
    widgets,
    isEditMode,
    reorderWidgets,
    updateWidget,
    removeWidget,
    addWidget,
    toggleEditMode,
  } = useDashboardLayout('retrait-analytics');
  const {
    withdrawals,
    loading: withdrawalsLoading,
    refetch: refetchWithdrawals
  } = useWithdrawals();
  const {
    products,
    loading: productsLoading,
    refreshProducts
  } = useSupabaseInventory();
  const {
    userRole
  } = useCompany();
  const {
    hasAccess
  } = useSubscription();

  useEffect(() => {
    document.title = 'Analyse des retraits — Chart-Flow';
  }, []);
  
  const lastUpdate = new Date().toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  
  const productById = useMemo(() => Object.fromEntries(products.map(p => [p.id, p])), [products]);
  
  // Filter for withdrawals (retraits): sales with total_amount = 0 and payment_method = 'Autre'
  const retraits = withdrawals;
  
  const filteredRetraits = useMemo(() => {
    secureLogger.debug('RetraitAnalytics', 'Filtering retraits', { 
      totalCount: retraits.length, 
      periodFilter, 
      statusFilter 
    });
    
    const {
      start,
      end
    } = getDateRangeFromPeriod(periodFilter);
    return retraits.filter(retrait => {
      const d = new Date(retrait.withdrawal_date);
      const inRange = d >= start && d <= end;
      if (!inRange) return false;
      if (statusFilter === 'all') return true;
      const prod = productById[retrait.product_id];
      return prod?.cycle_de_vie === statusFilter;
    });
  }, [retraits, periodFilter, statusFilter, productById]);
  
  const kpiData = useMemo(() => {
    const totalRetraits = filteredRetraits.length;
    const totalItemsRetired = filteredRetraits.reduce((sum, retrait) => sum + retrait.quantity_withdrawn, 0);
    const lowStockProducts = products.filter(product => product.quantity <= product.min_threshold && product.min_threshold > 0).length;
    const uniqueProducts = new Set(filteredRetraits.map(r => r.product_id)).size;
    
    // Most withdrawn products
    const productRetraitCount = filteredRetraits.reduce((acc, retrait) => {
      acc[retrait.product_id] = (acc[retrait.product_id] || 0) + retrait.quantity_withdrawn;
      return acc;
    }, {} as Record<string, number>);
    
    const averageRetraitPerProduct = uniqueProducts > 0 ? totalItemsRetired / uniqueProducts : 0;
    
    return {
      totalRetraits,
      totalItemsRetired,
      lowStockProducts,
      uniqueProducts,
      averageRetraitPerProduct,
      productRetraitCount
    };
  }, [filteredRetraits, products]);
  
  const handleRefresh = async () => {
    await Promise.all([refetchWithdrawals(), refreshProducts()]);
  };
  
  const isLoading = withdrawalsLoading || productsLoading;
  
  const getPeriodLabel = () => {
    switch (periodFilter) {
      case 'today':
        return "aujourd'hui";
      case 'week':
        return 'sur 7 jours';
      case 'month':
        return 'sur 30 jours';
      default:
        return 'depuis toujours';
    }
  };
  
  const totalPages = useMemo(() => Math.max(1, Math.ceil(filteredRetraits.length / rowsPerPage)), [filteredRetraits, rowsPerPage]);
  
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);
  
  useEffect(() => {
    setCurrentPage(1);
  }, [periodFilter, statusFilter, rowsPerPage]);

  return (
    <MainLayout title="Analyse des retraits">
      <div className="space-y-6">
        {/* Bannière supérieure */}
        <div className="bg-gradient-to-r from-orange-500/10 via-red-500/5 to-transparent rounded-lg p-4 lg:p-6 border">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                Analyse des retraits
              </h1>
              <p className="text-muted-foreground flex items-center gap-2 text-sm">
                <BarChart3 className="w-4 h-4" />
                Données actualisées au : {lastUpdate}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
              <PeriodFilter value={periodFilter} onChange={setPeriodFilter} showIcon />
              <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isLoading} className="flex items-center gap-2 w-full sm:w-auto">
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                <span className="sm:hidden">Actualiser</span>
                <span className="hidden sm:inline">Actualiser</span>
              </Button>
              <EditModeToggle isEditMode={isEditMode} onToggle={toggleEditMode} />
            </div>
          </div>
        </div>

        {/* Widgets éditables */}
        <EditableGrid
          widgets={widgets}
          onReorder={reorderWidgets}
          onConfigure={updateWidget}
          onRemove={removeWidget}
          isEditMode={isEditMode}
        >
          {(widget) => (
            <AnalyticsWidget
              widget={widget}
              kpiData={kpiData}
              filteredData={filteredRetraits}
              periodFilter={periodFilter}
              isLoading={isLoading}
              userRole={userRole}
              onStatusFilterChange={setStatusFilter}
              statusFilter={statusFilter}
              hasAccess={hasAccess}
              productById={productById}
              currentPage={currentPage}
              rowsPerPage={rowsPerPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              onRowsPerPageChange={(rows) => {
                setRowsPerPage(rows);
                setCurrentPage(1);
              }}
            />
          )}
        </EditableGrid>

        {/* Bouton d'ajout de widget */}
        {isEditMode && (
          <AddWidgetButton
            onAdd={(type, config) => addWidget(type, config)}
            availableWidgets={[
              { id: 'total-withdrawals', type: 'kpi', name: 'Total retraits', config: { kpiType: 'total-withdrawals' } },
              { id: 'items-withdrawn', type: 'kpi', name: 'Articles retirés', config: { kpiType: 'items-withdrawn' } },
              { id: 'products-concerned', type: 'kpi', name: 'Produits concernés', config: { kpiType: 'products-concerned' } },
              { id: 'average-per-product', type: 'kpi', name: 'Moyenne par produit', config: { kpiType: 'average-per-product' } },
              { id: 'withdrawals-pie', type: 'chart', name: 'Répartition des produits', config: { chartType: 'withdrawals-pie' } },
              { id: 'withdrawals-chart', type: 'chart', name: 'Évolution des retraits', config: { chartType: 'withdrawals-chart' } },
              { id: 'top-withdrawn-products', type: 'table', name: 'Produits les plus retirés', config: { tableType: 'top-withdrawn-products' } },
            ]}
          />
        )}
      </div>
    </MainLayout>
  );
};